import { FILE_JOB_MAX_FILES_PER_REQUEST, FileJobType } from '@atlas/types'
import { z } from 'zod'

/** Ceiling on a caller-supplied directory name — see `zipPackageGroupSchema`. */
const MAX_DIRECTORY_NAME_LENGTH = 255

/**
 * One packaging instruction on the wire: place these Files under this
 * directory, or at the archive root when `directoryName` is absent. Mirrors
 * `ZipPackageGroup` exactly, so the controller hands it to the source
 * untranslated.
 *
 * The directory name is NOT trusted as a path: `sanitizeDirectorySegments`
 * splits it on separators and drops `.`, `..` and control characters before it
 * reaches an archive entry, so traversal is structurally impossible whatever
 * arrives here. No further CONTENT validation is needed for that reason.
 *
 * Its LENGTH is bounded all the same, which sanitization says nothing about:
 * the name is copied into the `archivePath` of every entry the group names, and
 * those entries are persisted as one `file_job.payload` row and hashed whole for
 * the work fingerprint. Unbounded, a single request body amplifies by up to
 * `FILE_JOB_MAX_FILES_PER_REQUEST` before anything downstream can refuse it. The
 * ceiling is a generous real folder name, not a format limit.
 */
const zipPackageGroupSchema = z.object({
	directoryName: z.string().min(1).max(MAX_DIRECTORY_NAME_LENGTH).optional(),
	fileIds: z.array(z.uuid()).min(1),
})

/**
 * Schema for creating a File Job (HTTP POST /files/jobs body).
 *
 * `type` accepts only the client-creatable type (`FileJobType.ZIP`). A
 * system-registered type such as `ZIP_INSPECTION` is a valid `FileJobType` but
 * never creatable from a request body, so it is refused here like any other
 * unsupported value rather than reaching the service.
 * `idempotencyKey` is mandatory (unlike `fileCreateSchema`'s optional one):
 * every File Job is requester-scoped idempotent work, never a bare create.
 *
 * `groups` is the selected-Files ZIP source's transport shape: the caller names
 * the Files AND where each lands in the archive, because the folders an archive
 * carries are the ones the caller was looking at when it picked. A
 * competing `workOrderId`/domain-id field is deliberately never added here —
 * the core ZIP service never receives raw domain IDs, and a directory name is
 * not one: it is a literal, already-resolved path segment.
 *
 * The cap bounds the total across groups rather than one array's length, since
 * the same File named under two directories is two archive entries and two
 * copies of its bytes. It is the one place the selection size is capped, so
 * nothing downstream re-checks it.
 */
export const fileJobCreateSchema = z
	.object({
		type: z.literal(FileJobType.ZIP),
		idempotencyKey: z.uuid(),
		groups: z.array(zipPackageGroupSchema).min(1),
		downloadFileName: z.string().min(1).optional(),
	})
	.refine(
		(body) => body.groups.reduce((total, group) => total + group.fileIds.length, 0) <= FILE_JOB_MAX_FILES_PER_REQUEST,
		{
			path: ['groups'],
			message: `A File Job may name at most ${FILE_JOB_MAX_FILES_PER_REQUEST} Files across all groups`,
		},
	)

export type FileJobCreateInput = z.infer<typeof fileJobCreateSchema>
